import { useEffect } from 'react';
import { SUB_ACCOUNT_FAQ_ID, SUB_REPORT_FAQ_ID } from '../faq';
import { BackLink } from './BackLink';
import { PageShell } from './PageShell';
import { SectionCard } from './SectionCard';
import { SiteHeader } from './SiteHeader';

const LINK =
  'text-link cursor-pointer underline underline-offset-2 hover:opacity-75 focus-visible:outline-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2';

export function FaqPage() {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);

  return (
    <PageShell header={<SiteHeader title="FAQ" />}>
      <div className="flex flex-col gap-4">
        <BackLink href="/" label="Back to portal" />
        <p className="text-body px-1">
          Find help with your portal account and team.
        </p>
        <SectionCard title="What is FTLO Membership?">
          <p className="text-body">
            FTLO Membership is a status you earn by completing a full season as
            a registered participant in an FTLO clinic or Boba League.
          </p>
          <ul className="text-body flex list-disc flex-col gap-3 pl-6">
            <li>
              For Boba League, you must have been enrolled at the start of the
              season.
            </li>
            <li>Playing as a substitute doesn’t count.</li>
            <li>
              Open League or Summer Grass participation alone doesn’t qualify.
            </li>
            <li>Your Boba pin is proof of membership.</li>
            <li>Staff will verify eligibility.</li>
          </ul>
        </SectionCard>
        <SectionCard title="Sub declarations">
          <section className="flex flex-col gap-4">
            <h3 className="text-ink text-lg font-medium">
              Why do subs need an account?
            </h3>
            <p className="text-body">
              An account helps us confirm that every player has signed the
              waiver before stepping on court. It also helps us get a sense of
              each sub’s playing experience over time, so we can keep games fair
              and enjoyable for everyone.
            </p>
          </section>
          <section className="border-hairline flex flex-col gap-4 border-t pt-4">
            <h3
              id={SUB_ACCOUNT_FAQ_ID}
              tabIndex={-1}
              className="text-ink scroll-mt-6 text-lg font-medium"
            >
              <a href={`#${SUB_ACCOUNT_FAQ_ID}`} className={LINK}>
                How do I add a sub who doesn’t have an account?
              </a>
            </h3>
            <p className="text-body">
              Ask your sub to follow these steps, then try adding them again:
            </p>
            <ol className="text-body flex list-decimal flex-col gap-3 pl-6">
              <li>
                Go to{' '}
                <a href="https://portal.ftlovolleyball.app/" className={LINK}>
                  portal.ftlovolleyball.app
                </a>
                .
              </li>
              <li>
                Create an account with the email address your captain will use
                to add you.
              </li>
              <li>Complete your waiver and emergency contacts.</li>
              <li>
                Ask your captain to try adding you again with that same email
                address.
              </li>
            </ol>
            <p className="text-body">
              Already have an account? Ask your captain to check that they’re
              using the email address you signed up with.
            </p>
          </section>
          <section className="border-hairline flex flex-col gap-4 border-t pt-4">
            <h3
              id={SUB_REPORT_FAQ_ID}
              tabIndex={-1}
              className="text-ink scroll-mt-6 text-lg font-medium"
            >
              <a href={`#${SUB_REPORT_FAQ_ID}`} className={LINK}>
                How do I report a tournament sub?
              </a>
            </h3>
            <p className="text-body">
              Captains and co-captains of a team in the current season can
              report a sub. Open Declared subs and select the thumbs-down beside
              the sub’s name. Tell us why they aren’t suitable to sub at that
              level, for example they play above the team’s level. Staff review
              every report and see who filed it.
            </p>
            <ul className="text-body flex list-disc flex-col gap-3 pl-6">
              <li>
                If staff agree, they remove the sub from that team’s tournament
                subs. That team’s captain and co-captain get an email with
                staff’s reason. The email does not say who reported. If sub
                declarations are still open, the team can add a different sub.
                If they’re closed, the team contacts staff.
              </li>
              <li>
                If staff don’t agree, nothing changes and nobody is emailed.
              </li>
              <li>
                You won’t get a follow-up message about the outcome. The portal
                does not email the reported sub.
              </li>
            </ul>
          </section>
        </SectionCard>
      </div>
    </PageShell>
  );
}
